import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Copy, Download, FileText, Loader2, Mail, Plus, Send, Tag, Trash2, UserPlus,
} from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const blankRate = { origin: "", airport: "", price: "", vehicle_id: "", direction: "both" };

/** Everything about one corporate account: who may book, what they owe, and their statements. */
export const CorporateDetail = ({ accountId, onChange }) => {
  const [data, setData] = useState(null);
  const [traveller, setTraveller] = useState({ name: "", email: "", phone: "", cost_center: "" });
  const [rates, setRates] = useState([]);
  const [rate, setRate] = useState({ ...blankRate });
  const [month, setMonth] = useState(thisMonth());
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [portalUrl, setPortalUrl] = useState("");

  const load = useCallback(
    () => Promise.all([
      api.get(`/corporate-accounts/${accountId}`).then(({ data }) => setData(data)),
      api.get("/flat-rates", { params: { account_id: accountId } })
        .then(({ data }) => setRates(data.flat_rates)),
    ]).catch((e) => setError(errMsg(e))),
    [accountId]
  );
  useEffect(() => { load(); }, [load]);

  const act = async (key, run) => {
    setBusy(key); setError(""); setMsg("");
    try { await run(); await load(); onChange?.(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const addTraveller = () => act("traveller", async () => {
    await api.post(`/corporate-accounts/${accountId}/travellers`, traveller);
    setTraveller({ name: "", email: "", phone: "", cost_center: "" });
    setMsg("Traveller added — they can now book without a card.");
  });

  const generate = () => act("statement", async () => {
    const { data: s } = await api.post(`/corporate-accounts/${accountId}/statements`, { month });
    setMsg(`Statement #${s.number} for ${money(s.total)} created and emailed to ${s.billing_email}.`);
  });

  if (!data) {
    return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gold" /></div>;
  }
  const { account, trips, statements } = data;

  return (
    <div data-testid="corporate-detail" className="space-y-5 rounded-xl border border-gold/30 bg-ink-800 p-5 sm:p-6">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">{account.name}</p>
        <p className="mt-1 text-sm text-slate-400">
          {account.terms_label} · {money(account.outstanding)} unpaid
          {account.credit_limit > 0 ? ` of a ${money(account.credit_limit)} limit` : ""}
          {account.discount_percent > 0 ? ` · ${account.discount_percent}% off each trip` : ""}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button data-testid="corporate-portal-link" variant="outline" disabled={busy !== ""}
            onClick={() => act("portal", async () => {
              const { data: res } = await api.post(`/corporate-accounts/${accountId}/portal-link`);
              setPortalUrl(res.url);
              setMsg(res.emailed_to
                ? `Account page emailed to ${res.emailed_to} — they can manage their own travellers and download statements.`
                : "Account page link created — copy it below.");
            })}
            className="h-10 border-ink-600 bg-transparent text-slate-300 hover:text-gold">
            {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><Send className="mr-2 h-3.5 w-3.5" /> Email their self-service page</>}
          </Button>
          {portalUrl && (
            <button data-testid="corporate-portal-copy"
              onClick={() => { navigator.clipboard?.writeText(portalUrl); setMsg("Account page link copied."); }}
              className="flex h-10 items-center gap-1.5 rounded-md border border-ink-600 px-3 text-xs text-slate-300 hover:text-gold">
              <Copy className="h-3.5 w-3.5" /> Copy link
            </button>
          )}
        </div>
      </div>
      {error && <p data-testid="corporate-detail-error" className="text-sm text-red-400">{error}</p>}
      {msg && <p data-testid="corporate-detail-msg" className="text-sm text-emerald-400">{msg}</p>}

      <div>
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
          <UserPlus className="h-3.5 w-3.5" /> Authorised travellers
        </p>
        <div className="mt-3 space-y-2">
          {(account.travellers || []).length === 0 && (
            <p className="text-xs text-slate-500">
              Nobody yet. Add a traveller and any booking from their email goes on this account
              instead of asking for a card.
            </p>
          )}
          {(account.travellers || []).map((t) => (
            <div key={t.email} data-testid={`traveller-${t.email}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-ink-600 bg-ink-900 px-4 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm text-slate-100">{t.name}</p>
                <p className="truncate text-xs text-slate-500">
                  {t.email}{t.cost_center ? ` · ${t.cost_center}` : ""}
                </p>
              </div>
              <button data-testid={`traveller-remove-${t.email}`} title="Remove traveller"
                onClick={() => act(`rm-${t.email}`, () =>
                  api.delete(`/corporate-accounts/${accountId}/travellers/${encodeURIComponent(t.email)}`))}
                className="shrink-0 text-slate-500 transition-colors hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-4">
          <Input data-testid="traveller-name" placeholder="Name" value={traveller.name}
            onChange={(e) => setTraveller((t) => ({ ...t, name: e.target.value }))}
            className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
          <Input data-testid="traveller-email" placeholder="email@company.com" value={traveller.email}
            onChange={(e) => setTraveller((t) => ({ ...t, email: e.target.value }))}
            className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
          <Input data-testid="traveller-cost-center" placeholder="Cost centre (optional)" value={traveller.cost_center}
            onChange={(e) => setTraveller((t) => ({ ...t, cost_center: e.target.value }))}
            className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
          <Button data-testid="traveller-add" disabled={busy !== "" || !traveller.name || !traveller.email}
            onClick={addTraveller} className="h-11 bg-gold text-ink-900 hover:bg-gold-light">
            {busy === "traveller" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Add</>}
          </Button>
        </div>
      </div>

      <div className="border-t border-ink-600 pt-5">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
          <Tag className="h-3.5 w-3.5" /> Contract rates
        </p>
        <p className="mt-2 text-xs text-slate-500">
          A fixed price for one of their routes. It overrides both your public flat rate and the
          account discount, and is never shown to the public.
        </p>
        <div className="mt-3 space-y-2">
          {rates.length === 0 && (
            <p className="text-xs text-slate-500">
              No contract rates — trips on this account use your normal pricing
              {account.discount_percent > 0 ? ` less ${account.discount_percent}%` : ""}.
            </p>
          )}
          {rates.map((r) => (
            <div key={r.id} data-testid={`contract-rate-${r.id}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-ink-600 bg-ink-900 px-4 py-2.5">
              <p className="min-w-0 truncate text-sm text-slate-100">
                {r.origin} ↔ {r.airport}
                <span className="ml-2 font-mono text-xs text-gold">{money(r.price)}</span>
              </p>
              <button data-testid={`contract-rate-remove-${r.id}`} title="Remove contract rate"
                onClick={() => act(`rate-${r.id}`, () => api.delete(`/flat-rates/${r.id}`))}
                className="shrink-0 text-slate-500 transition-colors hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-4">
          <Input data-testid="contract-rate-origin" placeholder="Zone (e.g. Sugar Land)" value={rate.origin}
            onChange={(e) => setRate((r) => ({ ...r, origin: e.target.value }))}
            className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
          <Input data-testid="contract-rate-airport" placeholder="Airport (e.g. Intercontinental)" value={rate.airport}
            onChange={(e) => setRate((r) => ({ ...r, airport: e.target.value }))}
            className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
          <Input data-testid="contract-rate-price" type="number" min="1" step="5" placeholder="Price" value={rate.price}
            onChange={(e) => setRate((r) => ({ ...r, price: e.target.value }))}
            className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
          <Button data-testid="contract-rate-add"
            disabled={busy !== "" || !rate.origin || !rate.airport || !Number(rate.price)}
            onClick={() => act("rate", async () => {
              await api.post("/flat-rates", {
                ...rate, price: Number(rate.price), account_id: accountId,
                label: `Contract · ${account.name}`,
              });
              setRate({ ...blankRate });
              setMsg("Contract rate saved — it applies to this account's trips both ways.");
            })}
            className="h-11 bg-gold text-ink-900 hover:bg-gold-light">
            {busy === "rate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Add</>}
          </Button>
        </div>
      </div>

      <div className="border-t border-ink-600 pt-5">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
          <FileText className="h-3.5 w-3.5" /> Monthly statements
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Generated automatically on the 1st for the month before. You can also bill any month now.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input data-testid="statement-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="h-11 w-40 border-ink-600 bg-ink-900 text-slate-100" />
          <Button data-testid="statement-generate" disabled={busy !== ""} onClick={generate}
            className="h-11 bg-gold text-ink-900 hover:bg-gold-light">
            {busy === "statement" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Bill this month"}
          </Button>
        </div>
        <div className="mt-4 space-y-2">
          {statements.length === 0 && <p className="text-xs text-slate-500">No statements yet.</p>}
          {statements.map((s) => (
            <div key={s.id} data-testid={`statement-${s.number}`}
              className="rounded-lg border border-ink-600 bg-ink-900 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-100">
                    Statement #{s.number} · {s.month} · {money(s.total)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {s.trips} trip{s.trips === 1 ? "" : "s"} · {s.terms_label} · due {s.due_date}
                    {s.sent_at ? " · emailed" : ""}
                  </p>
                </div>
                {s.status === "paid" ? (
                  <span data-testid={`statement-paid-${s.number}`} className="flex items-center gap-1.5 text-sm text-emerald-300">
                    <CheckCircle2 className="h-4 w-4" /> Paid{s.payment_method ? ` by ${s.payment_method}` : ""}
                  </span>
                ) : (
                  <span className="rounded-full border border-amber-400/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-amber-300">
                    Open
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button data-testid={`statement-send-${s.number}`} variant="outline" disabled={busy !== ""}
                  onClick={() => act(`send-${s.id}`, async () => {
                    await api.post(`/statements/${s.id}/send`);
                    setMsg(`Statement #${s.number} emailed to ${s.billing_email}.`);
                  })}
                  className="h-10 border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                  <Mail className="mr-2 h-3.5 w-3.5" /> Email again
                </Button>
                <button data-testid={`statement-copy-${s.number}`}
                  onClick={() => { navigator.clipboard?.writeText(s.url); setMsg("Statement link copied."); }}
                  className="flex h-10 items-center gap-1.5 rounded-md border border-ink-600 px-3 text-xs text-slate-300 hover:text-gold">
                  <Copy className="h-3.5 w-3.5" /> Copy link
                </button>
                <a data-testid={`statement-pdf-${s.number}`}
                  href={`${API_BASE}/statements/${s.id}/pdf`} target="_blank" rel="noopener noreferrer"
                  className="flex h-10 items-center gap-1.5 rounded-md border border-ink-600 px-3 text-xs text-slate-300 hover:text-gold">
                  <Download className="h-3.5 w-3.5" /> PDF
                </a>
                {s.status !== "paid" && (
                  <Button data-testid={`statement-mark-paid-${s.number}`} variant="outline" disabled={busy !== ""}
                    onClick={() => act(`paid-${s.id}`, async () => {
                      await api.post(`/statements/${s.id}/mark-paid`, { method: "check", reference: "" });
                      setMsg(`Statement #${s.number} marked paid — every trip on it is now settled.`);
                    })}
                    className="h-10 border-ink-600 bg-transparent text-slate-300 hover:text-emerald-300">
                    Mark paid (check / ACH)
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-ink-600 pt-5">
        <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
          Recent trips on this account
        </p>
        <div className="mt-3 space-y-1.5">
          {trips.length === 0 && <p className="text-xs text-slate-500">No trips billed to this account yet.</p>}
          {trips.slice(0, 12).map((t) => (
            <Link key={t.id} to={`/app/manifests/${t.id}`} data-testid={`corporate-trip-${t.number}`}
              className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-ink-700">
              <span className="min-w-0">
                <span className="font-mono text-xs text-gold">#{t.number}</span>
                <span className="ml-2 text-slate-200">{t.customer_name}</span>
                <span className="ml-2 text-xs text-slate-500">{t.trip_date} {t.pickup_time}</span>
              </span>
              <span className="shrink-0 text-right">
                <span className="text-slate-100">{money(t.amount)}</span>
                <span className={`ml-2 font-mono text-[10px] uppercase tracking-widest ${
                  t.billed ? "text-slate-500" : "text-amber-300"}`}>
                  {t.billed ? "billed" : "unbilled"}
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};
